<template>
  <div
    :id="id"
    :class="$style.tags"
  >
    <div
      v-for="(option, key) in valueAsArray()"
      :key="key"
      :class="[$style.tag, {[$style.warningColor]: validateWarningWords(option.label), [$style.tagOutlined]: variant === 'outlined'} ]"
    >
      <fou-modal
        v-if="editable"
        :id="$vId(this)"
        cols="4"
      >
        <template #trigger="triggerScope">
          <span
            :id="`${id}-tag${key}`"
            class="mt-tag"
            :class="{ 'mt-tag--active': triggerScope.active }"
          >
            {{ option.label }}
            <button
              type="button"
              class="close"
              @click.stop="remove(key)"
            />
          </span>
        </template>

        <template #content>
          <div :class="$style.modalContent">
            <fou-group>
              <label
                :for="$vId(this, 'input')"
                class="mt-label mt-label--block"
              >
                {{ $t('components.forms.fou_select_tags.input.label') }}
              </label>
              <input
                :id="$vId(this, 'input')"
                v-model="option.edit"
                v-validate="'required'"
                type="text"
                :placeholder="$t('components.forms.fou_select_tags.input.placeholder')"
                name="tag_input"
                class="mt-input"
              >
            </fou-group>
            <fou-group v-if="errors?.has('tag_input')">
              <p class="mt-error">
                {{ error.first('tag_input') }}
              </p>
            </fou-group>
            <fou-group>
              <button
                type="button"
                class="mt-button mt-button--small"
                @click="save(option, key)"
              >
                {{ $t('components.forms.fou_select_tags.action.approve') }}
              </button>
            </fou-group>
          </div>
        </template>
      </fou-modal>
      <span
        v-if="!editable"
        :id="`${id}-tag${key}`"
        class="mt-tag mt-tag--hash"
        :class="[$style.relatedTags, {[$style.errorColor]: !validateScriptRegex(option.label), [$style.warningTextColor]: validateWarningWords(option.label), [$style.relatedTagsOutlined]: variant === 'outlined'}]"
      >
        {{ option.label }}
      </span>
      <button
          v-if="removable"
          type="button"
          class="close"
          :class="[$style.tagsClose, {[$style.warningTextColor]: validateWarningWords(option.label), [$style.tagsCloseOutlined]: variant === 'outlined'}]"
          @click="remove(key)"
        >
        ×
      </button>
    </div>
  </div>
</template>

<script type="text/ecmascript-6">
import FouModal from 'components/overlay/fou-modal/'
import FouGroup from 'components/layout/fou-group/'
import {mapActions} from 'vuex'
import Misc from 'src/utils/helper/misc'

export default {
  name: 'FouSelectTags',
  
  components: {
    FouModal,
    FouGroup
  },

  props: {
    fouSelect: {
      type: Object,
      required: true
    },
    fouSelectId: {
      type: String,
      default: null
    },
    editable: {
      type: Boolean,
      default: false
    },
    tagsCustom: {
      type: Boolean,
      default: false
    },
    customColor: {
      type: Object,
      default: () => ({})
    },
    removable: {
      type: Boolean,
      default: true
    },
    warningWords: {
      type: Array,
      default: () => []
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'outlined'].includes(value)
    }
  },

  created() {
    this.$events?.$on('fou-select-tags:update', this.update)
  },

  beforeUnmount() {
    this.$events.$off('fou-select-tags:update', this.update)
  },

  methods: {
    ...mapActions('overlay', ['hide']),
    ...Misc,
    validateWarningWords(value) {
      const lowerCaseValue = value.toLowerCase();
      return this.warningWords?.some(element => element.toLowerCase() === lowerCaseValue);
    },
    remove(key) {
      this.fouSelect.remove(key)
      this.$emit('on:remove', key)
      this.$events.$emit('fou-select:update', this.fouSelectId)
      document.activeElement.blur();
    },
    update() {
      /**
       * In case of non-reactive data, make sure data is updated, too.
       */
      this.$forceUpdate()
    },
    save(option) {
      /**
       * This.fouSelect.values is reactive.
       * By overriding it, on-change of fou-select will be triggered.
       */
      this.fouSelect.values = this.fouSelect.values.map(v => {
        if (v.value === option.value) {
          v.label = option.edit
        }
        return v
      })
      this.fouSelect.options.some(o => {
        if (o.value === option.value) {
          o.label = option.edit
          return true
        }
      })
      this.hide()
    },
    valueAsArray() {
      const values = this.fouSelect?.values || []
      values.forEach(v => {
        v.edit = v.label
      })
      return values
    }
  }
}
</script>

<style lang="scss" module>
@import 'mpl-css/lib/assets/global/functions/index';
@import 'mpl-css/lib/assets/global/variables/colors';
@import 'mpl-css/lib/assets/global/mfpfonts/fonts.scss';

.tags {
  display: flex;
  flex-wrap: wrap;
}

.tag {
  display: flex;
  justify-content: center;
  align-items: center;
  height: rem(32);
  width: fit-content;
  background-color: #1A73E8;
  border-radius: 16px;
  padding: rem(8px) rem(8px) rem(8px) rem(12px);
  gap: rem(8px);
  margin: 0.25rem;
}

.tagsClose {
  margin: 0;
  padding: 0;
  color: #fff;
  height: rem(16px);
  width: rem(16px);
  display: flex;
  justify-content: center;
  align-items: center;
}
.modalContent {
  padding: rem(20);
}

.warningTextColor {
  color: map-get($colors, 'spirits--900');
}

.errorColor {
  background-color: #e3152e;
}
.relatedTags {
  @extend .mfp-HelveticaNowMTText-Medium-13;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  margin: 0;
  border: none;
  padding: 0;
}
.tagOutlined {
  background-color: var(--md-color--spirits--25, #fcfcfd);
  border: 1px solid var(--md-color--spirits--500, #c3cad4);
  margin: 0;
}

.tagsCloseOutlined {
  color: var(--md-color--spirits--600, #667488);
  background: none;
  border: none;
  cursor: pointer;
}

.relatedTagsOutlined {
  color: var(--md-color--spirits--700, #556174);
}

.warningColor {
  background-color: map-get($colors, 'orange-squash-25');
  border : 1px solid map-get($colors, 'orange-squash-500');
  color: map-get($colors, 'spirits--900');
}
</style>
